/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: hooks/useUserActivityList.ts
 🗓️ Created: 2025-12-02
 🧩 Description:
    Hook for managing User Activity list
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useState } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAuthStore } from '../../../store/auth';
import { fetchUserActivity } from '../api/userActivityApi';
import { UserActivity, ListUserActivityParams, UserActivityFilterValues } from '../types/userActivity.types';

/**
 * Hook for managing User Activity list
 */
export function useUserActivityList() {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { user } = useAuthStore();
  const tenantSlug = user?.tenant?.slug || 'main';
  const userId = user?.id;

  // Filter state
  const [filters, setFilters] = useState<UserActivityFilterValues>({
    startDate: '',
    endDate: '',
    actions: [],
    resource: '',
    domainId: '',
  });

  const fetchData = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    try {
      const queryParams: ListUserActivityParams = {
        page: params.page,
        limit: params.limit,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        search: params.search,
        // Add filters to query params
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        action: filters.actions.length > 0 ? filters.actions.join(',') : undefined,
        resource: filters.resource || undefined,
        domainId: filters.domainId ? parseInt(filters.domainId, 10) : undefined,
      };

      if (!userId) {
        throw new Error('User ID not available');
      }

      const result = await fetchUserActivity(queryParams, tenantSlug, userId);
      
      return {
        data: result.data,
        pagination: result.pagination
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : t('userActivity.messages.loadError');
      showError(message);
      throw error;
    }
  }, [showError, t, filters, tenantSlug, userId]);

  const availableColumns = useMemo(() => [
    { key: 'id', label: t('userActivity.columns.id'), visible: false, sortable: false },
    { key: 'createdAt', label: t('userActivity.columns.timestamp'), visible: true, sortable: true },
    { key: 'action', label: t('userActivity.columns.action'), visible: true, sortable: true },
    { key: 'resource', label: t('userActivity.columns.resource'), visible: true, sortable: true },
    { key: 'resourceId', label: t('userActivity.columns.resourceId'), visible: false, sortable: false },
    { key: 'ipAddress', label: t('userActivity.columns.ipAddress'), visible: true, sortable: false },
    { key: 'userAgent', label: t('userActivity.columns.userAgent'), visible: false, sortable: false },
    { key: 'metadata', label: t('userActivity.columns.metadata'), visible: false, sortable: false },
  ], [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'createdAt',
    initialSortOrder: 'desc',
    initialLimit: 50,
    moduleName: 'userActivity'
  });

  // Filter handlers
  const handleFilterChange = useCallback((newFilters: UserActivityFilterValues) => {
    setFilters(newFilters);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      startDate: '',
      endDate: '',
      actions: [],
      resource: '',
      domainId: '',
    });
    setTimeout(() => refresh(), 100);
  }, [refresh]);

  const handleApplyFilters = useCallback(() => {
    refresh();
  }, [refresh]);

  return {
    data: data as UserActivity[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    filters,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    handleFilterChange,
    handleClearFilters,
    handleApplyFilters,
    refresh
  };
}

